import React, {Component, useState, useRef, useEffect} from "react"
import {
  Button,
  Divider,
  Text,
  HStack,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Box,
  useToast,
  VStack,
} from "@chakra-ui/react"
import {
  useNavigate
} from 'react-router-dom';
import { DeleteIcon, AddIcon, ExternalLinkIcon } from '@chakra-ui/icons'

import { backendURL } from '../../App';
import { useCsrf } from '../../hooks/useCsrf';

class Sidebar extends Component {
  render () {
    return (
      <Box
        position="relative"
        height="calc(100vh - 16px)"
        display="flex"
        flexDirection="column"
      >
        {/* New Chapter button - outside of List to avoid spacing issues */}
        <Box px={4} py={3}>
          <NewChapterLine onCreateChapter={this.props.onCreateChapter} />
        </Box>
        <Divider borderColor="#E4E4E7" mx={4} w="auto" />

        {/* Scrollable chapters section */}
        <Box flex="1" overflow="auto" pb={20}>
          <VStack spacing={1} px={4} py={3} align="stretch">
            <ChaptersList
              state={this.props.state}
              onViewChapter={this.props.onViewChapter}
              onDeleteChapter={this.props.onDeleteChapter}
              onSaveChapterTitle={this.props.onSaveChapterTitle}
              onFormTextChangeUpdate={this.props.onFormTextChangeUpdate}
            />
          </VStack>
        </Box>

        {/* Fixed bottom section */}
        <Box
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          p={4}
          bg="white"
          borderTop="1px solid"
          borderColor="#E4E4E7"
        >
          <VStack spacing={3}>
            <PublishButton bookId={this.props.state.chapters.book_id} />
            <Button
              variant="ghost"
              size="sm"
              width="100%"
              color="#A1A1AA"
              fontWeight="500"
              fontSize="sm"
              _hover={{
                bg: '#FEF2F2',
                color: '#52525B'
              }}
              transition="all 0.2s"
              onClick={() => window.location.href = `mailto:support@writewizard.org`}
            >
              Contact
            </Button>
          </VStack>
        </Box>
      </Box>
    )
  }
}

function PublishButton({ bookId }) {
  const [isPublished, setIsPublished] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const { authenticatedFetch } = useCsrf();

  useEffect(() => {
    const checkPublishStatus = async () => {
      try {
        const response = await fetch(`${backendURL}/explore/check_story_published/${bookId}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.success) {
          setIsPublished(data.isPublished);
        }
      } catch (error) {
        console.error('Error checking publish status:', error);
      }
    };

    if (bookId) {
      checkPublishStatus();
    }
  }, [bookId]);

  const handlePublishClick = async () => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      const response = await authenticatedFetch(`${backendURL}/explore/${isPublished ? 'unpublish_book' : 'publish_book'}/${bookId}`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setIsPublished(!isPublished);
        toast({
          title: 'Success',
          description: data.message,
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      } else {
        throw new Error(data.error || 'Failed to publish story');
      }
    } catch (error) {
      console.error('Error publishing story:', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      leftIcon={<ExternalLinkIcon />}
      variant="outline"
      size="sm"
      width="100%"
      onClick={handlePublishClick}
      isLoading={isLoading}
      loadingText={isPublished ? "Unpublishing..." : "Publishing..."}
      color={isPublished ? "white" : "#52525B"}
      borderColor={isPublished ? "#E05252" : "#E4E4E7"}
      borderRadius="lg"
      fontWeight="500"
      fontSize="sm"
      bg={isPublished ? '#E05252' : 'transparent'}
      _hover={{
        bg: isPublished ? '#C94545' : '#FEF2F2',
        borderColor: isPublished ? '#C94545' : '#E05252'
      }}
      transition="all 0.2s"
    >
      {isPublished ? 'Unpublish Story' : 'Publish Story'}
    </Button>
  );
}

function ChaptersList ({
  state,
  onViewChapter,
  onDeleteChapter,
  onSaveChapterTitle,
  onFormTextChangeUpdate
}) {
    let chapters = state.chapters
    return (
    <>
      {chapters['exists'] === true
      ?
      chapters['items'].map(chapter => (
          <ChapterLine
          id = {chapter.id}
          title = {chapter.title}
          chapter = {chapter}
          onViewChapter = {onViewChapter}
          onDeleteChapter = {onDeleteChapter}
          onSaveChapterTitle = {onSaveChapterTitle}
          onFormTextChangeUpdate = {onFormTextChangeUpdate}
          />
        )
      )
      :
        null
      }
    </>
    )
}

function NewChapterLine ({onCreateChapter}) {
    return (
      <HStack
        spacing="8px"
        align="center"
        _hover={{ bg: '#FEF2F2', cursor: "pointer" }}
        borderRadius="lg"
        transition="all 0.15s"
        onClick={onCreateChapter}
        h="36px"
        px="10px"
      >
        <AddIcon color="#E05252" boxSize="10px" flexShrink={0} />
        <Text color="#E05252" fontWeight="600" fontSize="sm">New Chapter</Text>
      </HStack>
    )
}

function ChapterLine ({id, title, chapter, onViewChapter, onDeleteChapter, onSaveChapterTitle, onFormTextChangeUpdate}) {
    const navigate = useNavigate()
    return (
      <HStack
        justify='space-between'
        align="center"
        px="10px"
        h="36px"
        borderRadius="lg"
        cursor="pointer"
        _hover={{ bg: '#F5F3F0' }}
        transition="all 0.15s"
        onClick={() => {
          navigate(`/chapter/${id}`)
          onViewChapter(id)
        }}
      >
        <Text
          color='#52525B' fontWeight="500" fontSize="sm"
          noOfLines={1}
        >{title}
        </Text>
        {chapter.is_outline
        ?
            null
          :
            <DeleteChapterIcon
            id = {id}
            onDeleteChapter = {onDeleteChapter}
            />
          }
      </HStack>
    )
  }

const DeleteChapterIcon = ({ onDeleteChapter, id }) => {
  const [isOpen, setIsOpen] = useState(false);
  const cancelRef = useRef();

  const openDialog = () => setIsOpen(true);
  const closeDialog = () => setIsOpen(false);

  const handleDelete = (id) => {
    onDeleteChapter(id);
    closeDialog();
  };

  return (
    <>
      <DeleteIcon
        mr='4px'
        color="#A1A1AA"
        boxSize="12px"
        cursor='pointer'
        _hover={{ color: '#E05252' }}
        transition="color 0.15s"
        onClick={(e) => {
          e.stopPropagation();
          openDialog()
        }}
        />

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={closeDialog}
      >
        <AlertDialogOverlay bg="blackAlpha.400" backdropFilter="blur(4px)">
          <AlertDialogContent borderRadius="xl">
            <AlertDialogHeader fontSize="lg" fontWeight="600">
              Delete Chapter
            </AlertDialogHeader>

            <AlertDialogBody color="#52525B">
              Are you sure you want to delete this chapter? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={closeDialog} borderRadius="lg">
                Cancel
              </Button>
              <Button bg="#E05252" color="white" _hover={{ bg: '#C94545' }} borderRadius="lg" onClick = {() => {handleDelete(id)}} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};



export default Sidebar
